// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart, updateCart } from "@insite/client-framework/Services/CartService";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import loadCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCart";
import translate from "@insite/client-framework/Translate";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Checkbox, { CheckboxPresentationProps } from "@store/MobiusOverrides/Checkbox/Checkbox";
import { useLocalStorage } from "@store/Framework/Common/Hooks/useLocalStorage";
import { cartFulfillmentMethodStyles } from "@store/Overrides/Widgets/Cart/CartFulfillmentMethodSelector";
import { useCurrentCartState } from "@toolkit/Framework/Store/Data/Carts/CartsHooks";
import React, { SyntheticEvent, useRef } from "react";
import { css } from "styled-components";

interface FlatFeeSelectorStyles {
    boxWrapper?: InjectableCss;
    checkbox?: CheckboxPresentationProps;
    wrapper?: InjectableCss;
}

export const flatFeeSelectorStyles: FlatFeeSelectorStyles = {
    boxWrapper: cartFulfillmentMethodStyles.boxWrapper,
    wrapper: cartFulfillmentMethodStyles.fulfillmentMethodWrapper,
    checkbox: {
        iconProps: {
            size: 26,
        },
        css: css`
            > label {
                font-size: ${pxToRem(18)};
                margin-top: ${pxToRem(3)};
            }
        `,
    },
};

interface Props extends WidgetProps {}

type StoredValue = boolean;

const FlatFeeSelector: React.FC<Props> = () => {
    const [updateCartLoading, setUpdateCartLoading] = React.useState(false);
    const dispatch = useAppDispatch();

    // hook assumes that value can be undefined but we're setting a default value
    const [storedValue, setStoredValue] = useLocalStorage<StoredValue>("mobile-van-flat-fee", false) as [
        StoredValue,
        (value: StoredValue) => void,
    ];
    const hasHandledDefault = React.useRef<boolean>(false);
    const isLoading = useAppSelector(state => state.data.carts.isLoading.current);
    const cartState = useCurrentCartState();
    const cart = cartState.value;
    const styles = flatFeeSelectorStyles;
    const cartFuelChargeValue = cart?.properties.addFuelSurcharge
        ? cart.properties.addFuelSurcharge === "true"
        : undefined;
    const isChecked = storedValue ?? cartFuelChargeValue ?? false;

    const handleChangeFuelSurchargeValue = async (value: StoredValue) => {
        if (!cart) {
            return;
        }
        setUpdateCartLoading(true);
        await updateCart({ cart: { ...cart, properties: { ...cart?.properties, addFuelSurcharge: String(value) } } });
        setUpdateCartLoading(false);
        dispatch(loadCart({ cartId: cart.id, shouldLoadFullCart: true }));
    };

    const handleChange = (event: SyntheticEvent<Element, Event>, value: boolean) => {
        if (!cart) {
            return;
        }
        setStoredValue(value);
        handleChangeFuelSurchargeValue(value);
    };

    const handleDefaultValue = (storedValue: StoredValue) => {
        if (hasHandledDefault.current) {
            return;
        }
        hasHandledDefault.current = true;
        if (cartFuelChargeValue !== undefined) {
            setStoredValue(cartFuelChargeValue);
            return;
        }
        if (cartFuelChargeValue !== storedValue && storedValue !== undefined) {
            handleChangeFuelSurchargeValue(storedValue);
        }
    };

    if (!cart) {
        return null;
    }

    if (!isLoading) {
        handleDefaultValue(storedValue);
    }

    return (
        <StyledWrapper {...styles?.wrapper}>
            <Checkbox
                disabled={isLoading || updateCartLoading}
                onChange={handleChange}
                {...styles?.checkbox}
                checked={isChecked}
            >
                {translate("Add a shipping charge")}
            </Checkbox>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: FlatFeeSelector,
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
    },
};

export default widgetModule;
